import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Color metaobject search support.
 *
 * The store exposes its paint colors as Shopify metaobjects (the same data that
 * powers the color overview page). We load them client-side via the Storefront
 * API once per session (cached) so typing in the search modal can surface
 * matching colors alongside product results.
 */

/** @typedef {{ name_nl?: string, name_en?: string, name_fr?: string, hex?: string, collection?: string, url?: string }} ColorEntry */

const COLOR_TYPES = [
  { type: 'pimcore_paintfactory_colors', label: 'PaintFactory' },
  { type: 'pimcore_paintfactory_miniral_colors', label: 'Miniral' },
  { type: 'pimcore_ral_colors', label: 'RAL' },
  { type: 'pimcore_nsc_colors', label: 'NCS' },
  { type: 'tintology_x_francq_colors', label: 'Francq Colors' },
];

/* Miniral colors are a curated subset that also live in the PaintFactory set, so
   we hide the Miniral duplicates and only surface the PaintFactory version. */
const COLOR_DEDUPE_COLLECTION = 'Miniral';
const COLOR_DEDUPE_AGAINST = 'PaintFactory';
const COLOR_CACHE_KEY = 'pf:color-search:v2';
const COLOR_RESULT_LIMIT = 12;
const COLOR_GQL_QUERY =
  'query($type:String!,$first:Int!,$after:String){metaobjects(type:$type,first:$first,after:$after){edges{node{onlineStoreUrl fields{key value}}}pageInfo{hasNextPage endCursor}}}';

/**
 * Module-level promise so the (potentially large) color set is only fetched once
 * per page load, regardless of how many times the search modal is opened.
 * @type {Promise<ColorEntry[]> | null}
 */
let colorLoadPromise = null;

/**
 * Keep absolute Storefront API URLs on the current origin so links work when
 * previewing on a different host (e.g. the theme dev server).
 * @param {string} url
 * @returns {string}
 */
function toRelativeColorUrl(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search + parsed.hash;
  } catch (error) {
    return url;
  }
}

/**
 * @param {{ onlineStoreUrl?: string, fields: { key: string, value: string }[] }} node
 * @returns {ColorEntry}
 */
function parseColorNode(node) {
  /** @type {ColorEntry} */
  const color = { url: toRelativeColorUrl(node.onlineStoreUrl || '') };
  for (const field of node.fields) {
    if (field.key === 'hex_color') color.hex = field.value || '';
    else if (field.key === 'name_nl') color.name_nl = field.value || '';
    else if (field.key === 'name_en') color.name_en = field.value || '';
    else if (field.key === 'name_fr') color.name_fr = field.value || '';
  }
  return color;
}

/**
 * Fetch every metaobject of a given type via the Storefront API (paginated).
 * @param {string} shopDomain
 * @param {string} token
 * @param {string} metaType
 * @param {string} label
 * @returns {Promise<ColorEntry[]>}
 */
async function fetchColorType(shopDomain, token, metaType, label) {
  const apiUrl = `https://${shopDomain}/api/2026-04/graphql.json`;
  /** @type {ColorEntry[]} */
  const colors = [];
  let cursor = null;

  while (true) {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query: COLOR_GQL_QUERY, variables: { type: metaType, first: 250, after: cursor } }),
    });
    const json = await response.json();
    const data = json?.data?.metaobjects;
    if (!data) break;

    for (const edge of data.edges) {
      const color = parseColorNode(edge.node);
      color.collection = label;
      colors.push(color);
    }

    if (data.pageInfo?.hasNextPage) {
      cursor = data.pageInfo.endCursor;
    } else {
      break;
    }
  }

  return colors;
}

/**
 * Load all colors once, using a sessionStorage cache to avoid refetching on
 * subsequent page loads within the same session.
 * @param {{ shopDomain: string, token: string }} config
 * @returns {Promise<ColorEntry[]>}
 */
function loadAllColors({ shopDomain, token }) {
  if (colorLoadPromise) return colorLoadPromise;

  colorLoadPromise = (async () => {
    try {
      const cached = sessionStorage.getItem(COLOR_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch (error) {
      /* ignore cache read errors */
    }

    if (!shopDomain || !token) return [];

    try {
      const groups = await Promise.all(
        COLOR_TYPES.map((type) => fetchColorType(shopDomain, token, type.type, type.label))
      );
      const all = groups.flat();
      try {
        sessionStorage.setItem(COLOR_CACHE_KEY, JSON.stringify(all));
      } catch (error) {
        /* sessionStorage may be full or unavailable */
      }
      return all;
    } catch (error) {
      console.warn('[predictive-search] Color load failed:', error);
      colorLoadPromise = null; // allow a retry on the next search
      return [];
    }
  })();

  return colorLoadPromise;
}

/**
 * @param {ColorEntry} color
 * @param {string} locale
 * @returns {string}
 */
function getColorName(color, locale) {
  if (locale === 'nl') return color.name_nl || color.name_en || '';
  if (locale === 'fr') return color.name_fr || color.name_en || '';
  return color.name_en || color.name_nl || '';
}

/**
 * Key used to detect the same color across collections (name + hex).
 * @param {ColorEntry} color
 * @returns {string}
 */
function colorDedupeKey(color) {
  const name = (color.name_nl || color.name_en || color.name_fr || '').toLowerCase().trim();
  return `${name}|${(color.hex || '').toLowerCase().trim()}`;
}

/**
 * @param {ColorEntry[]} colors
 * @param {string} query
 * @param {string} locale
 * @param {number} limit
 * @returns {ColorEntry[]}
 */
function filterColors(colors, query, locale, limit) {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return [];

  /** @type {Record<string, boolean>} */
  const paintFactoryKeys = {};
  for (const color of colors) {
    if (color.collection === COLOR_DEDUPE_AGAINST) paintFactoryKeys[colorDedupeKey(color)] = true;
  }

  /** @type {ColorEntry[]} */
  const matches = [];
  for (const color of colors) {
    if (color.collection === COLOR_DEDUPE_COLLECTION && paintFactoryKeys[colorDedupeKey(color)]) continue;
    if (getColorName(color, locale).toLowerCase().indexOf(normalized) === -1) continue;
    matches.push(color);
    if (matches.length >= limit) break;
  }
  return matches;
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeColorHtml(value) {
  const div = document.createElement('div');
  div.textContent = value || '';
  return div.innerHTML;
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Color search configuration, read from data attributes.
   * @type {{ enabled: boolean, shopDomain: string, token: string, locale: string, heading: string }}
   */
  #colorConfig = { enabled: false, shopDomain: '', token: '', locale: 'nl', heading: 'Colors' };

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    this.#colorConfig = {
      enabled: this.dataset.colorSearchEnabled === 'true' && Boolean(this.dataset.storefrontToken),
      shopDomain: this.dataset.shopDomain || '',
      token: this.dataset.storefrontToken || '',
      locale: this.dataset.locale || 'nl',
      heading: this.dataset.colorsHeading || 'Colors',
    };

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }

    // Warm the color cache so results are ready by the time the user types.
    if (this.#colorConfig.enabled) {
      loadAllColors({ shopDomain: this.#colorConfig.shopDomain, token: this.#colorConfig.token });
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#renderColorResults(searchTerm);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Filter the loaded colors by the search term and inject matching color cards
   * into the predictive search results. The product results are server-rendered
   * and morphed on every keystroke, so this re-injects colors after each morph.
   * @param {string} searchTerm - The term the user searched for.
   */
  async #renderColorResults(searchTerm) {
    if (!this.#colorConfig.enabled) return;

    const colors = await loadAllColors({ shopDomain: this.#colorConfig.shopDomain, token: this.#colorConfig.token });

    // Bail if the input changed while colors were loading.
    if (this.refs.searchInput.value.trim() !== searchTerm) return;

    const inner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
    if (!inner) return;

    inner.querySelector('[data-color-results]')?.remove();

    const matches = filterColors(colors, searchTerm, this.#colorConfig.locale, COLOR_RESULT_LIMIT);
    if (matches.length === 0) return;

    // We have color matches, so drop the product "no results" message if shown.
    inner.querySelector('.predictive-search-results__no-results')?.remove();

    const section = document.createElement('div');
    section.className = 'predictive-search-results__colors';
    section.setAttribute('data-color-results', '');

    const items = matches
      .map((color) => {
        const name = getColorName(color, this.#colorConfig.locale);
        const hex = color.hex || '';
        const url = color.url || '';
        const collection = color.collection || '';
        const swatch = `<span class="color-card__swatch" style="background-color:${escapeColorHtml(hex)};" aria-hidden="true"></span>`;
        const info =
          `<span class="color-card__info"><span class="color-card__name">${escapeColorHtml(name)}</span>` +
          (collection ? `<span class="color-card__collection">${escapeColorHtml(collection)}</span>` : '') +
          `</span>`;
        const card = url
          ? `<a class="color-card color-card--interactive" href="${escapeColorHtml(url)}" style="--color-card-ratio: 1 / 1;">${swatch}${info}</a>`
          : `<span class="color-card color-card--interactive" style="--color-card-ratio: 1 / 1;">${swatch}${info}</span>`;
        return `<li class="predictive-search-results__color-card" ref="resultsItems[]">${card}</li>`;
      })
      .join('');

    section.innerHTML =
      `<h4 class="predictive-search-results__title">${escapeColorHtml(this.#colorConfig.heading)}</h4>` +
      `<ul class="predictive-search-results__list predictive-search-results__colors-grid list-unstyled" role="listbox" aria-label="${escapeColorHtml(this.#colorConfig.heading)}">${items}</ul>`;

    inner.appendChild(section);
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
